import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilterList,
  faPenToSquare,
  faCirclePlus,
  faCircleMinus,
} from "@fortawesome/pro-regular-svg-icons";
import useExporter from "../../hooks/useExporter";
import { Link, useNavigate, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Modal from "../../components/Modal";
import PriceFormatter from "../../helpers/PriceFormatter";
import Dropdown from "../../components/Dropdown";
import {
  getUsersByTenant,
  deleteUser,
  updateBillingGroupOnUser,
  getScoFormSettings,
} from "../../api/Users";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import TableView from "../../components/TableView";
import { getAgreementByPublicId, getAccountTypesByAgreement } from "../../api/Agreement";
import MultipleSelectDropdown from "../../components/MultipleSelectDropdown";
import Switch from "../../components/Switch";

const UsersPage = () => {
  const { tenantPublicId, agreementPublicId } = useParams();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();

  const [users, setUsers] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [billingGroups, setBillingGroups] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedBillingGroup, setSelectedBillingGroup] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateUserOpen, setIsCreateUserOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => { },
  });
  const [agreement, setAgreement] = useState(null);
  const [scoFormSettings, setScoFormSettings] = useState(null);

  const handleOpenModal = () => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleExportToExcel = () => {
    const usersToExport =
      selectedUsers.length > 0
        ? filteredUsers.filter((user) =>
          selectedUsers.includes(user.userPublicId)
        )
        : filteredUsers;

    if (usersToExport.length === 0) {
      toast.error("No users selected");
      return;
    }
    const usersData = usersToExport.map(
      ({
        userPublicId,
        licenses,
        displayName,
        userPrincipalName,
        officeLocation,
        country,
        mobilePhone,
        jobTitle,
        managerDisplayName,
        lastSignInDateTime,
        OnPremisesSyncEnabled,
        accountTypeName,
        accountTypePrice,
        accountTypeStatus,
        billingGroupName,
        billingGroupStatus,
        createdBy,
        createdDateTimeUTC,
        updatedBy,
        updatedDateTimeUTC,
        userGroups,
        userDevices,
        entraTenantId,
      }) => ({
        "User Id": userPublicId,
        Licenses: licenses,
        Name: displayName,
        Username: userPrincipalName,
        "Office location": officeLocation,
        Country: country,
        Phone: mobilePhone,
        "Job title": jobTitle,
        Manager: managerDisplayName,
        "Last login": lastSignInDateTime,
        "On-prem sync": OnPremisesSyncEnabled,
        "Accout name": accountTypeName,
        "Account type price": accountTypePrice,
        "Account status": accountTypeStatus,
        "Billing group name": billingGroupName,
        "Billing group status": billingGroupStatus,
        Groups: userGroups,
        "User devices": userDevices,
        "Createdb by": createdBy,
        "Created date": createdDateTimeUTC,
        "Updated by": updatedBy,
        "Updated date": updatedDateTimeUTC,
        "Entra tenant id": entraTenantId,
      })
    );

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Users_${currentDate}`;

    handleExport({ Users: usersData }, filename);
  };

  const handleChangeBillingGroup = async () => {
    if (!selectedBillingGroup || selectedUsers.length === 0) {
      toast.error("Please select users and a billing group.");
      return;
    }

    try {
      const accessToken = await getAccessToken();

      await Promise.all(
        selectedUsers.map(async (userPublicId) => {
          return updateBillingGroupOnUser(
            selectedBillingGroup,
            userPublicId,
            accessToken
          );
        })
      );

      toast.success("Billing group updated successfully for selected users!", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });

      handleGetUsers();
      setSelectedUsers([]);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to update billing group:", error);
      toast.error("Failed to update billing group for selected users.");
    }
  };

  const handleSelectBillingGroup = (billingGroupName) => {
    setSelectedBillingGroup(
      billingGroups.find((group) => group.name === billingGroupName)
        ?.billingGroupPublicId || null
    );
  };

  useEffect(() => {
    handleGetUsers();
    handleGetAgreement();
    handleGetScoFormSettings();
    const handleUserUpdate = () => {
      handleGetUsers();
    };

    window.addEventListener("userUpdated", handleUserUpdate);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, [tenantPublicId, agreementPublicId]);

  const [filters, setFilters] = useState({
    billingGroup: [],
    accountType: [],
    role: [],
    licenses: [],
    lastSignInDateTimeUtc: "",
    onPremSyncEnabled: null,
  });

  const onPremOptions = [
    { label: "All", value: null },
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = users;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = users.filter(
          (user) =>
            user.userId?.toLowerCase().includes(searchLower) ||
            user.displayName?.toLowerCase().includes(searchLower) ||
            user.userPrincipalName?.toLowerCase().includes(searchLower) ||
            user.accountTypeName?.toLowerCase().includes(searchLower) ||
            user.billingGroupName?.toLowerCase().includes(searchLower)
        );
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      if (filters.accountType.length > 0) {
        filtered = filtered.filter((user) =>
          filters.accountType.includes(user.accountTypeName)
        );
      }
      if (filters.billingGroup.length > 0) {
        filtered = filtered.filter((user) =>
          filters.billingGroup.includes(user.billingGroupName)
        );
      }
      if (filters.onPremSyncEnabled !== null) {
        filtered = filtered.filter(
          (user) => user.onPremSyncEnabled === filters.onPremSyncEnabled
        );
      }
      if (filters.role.length > 0) {
        filtered = filtered.filter((user) => {
          const userRoles = user.roles
            ? user.roles
              .split(",")
              .map((r) => r.trim())
              .filter(Boolean)
            : [];

          const hasNoRoles = userRoles.length === 0;

          return filters.role.some((role) => userRoles.includes(role.toLowerCase())
          );
        });
      }
      if (Array.isArray(filters.licenses) && filters.licenses.length > 0) {
        filtered = filtered.filter((user) => {
          const userLicenses = user.licenses
            ? user.licenses
              .split(", ")
              .map((l) => l.trim())
              .filter(Boolean)
            : [];

          const hasNoLicenses = userLicenses.length === 0;

          return filters.licenses.some((lic) => userLicenses.includes(lic));
        });
      }
      if (filters.lastSignInDateTimeUtc) {
        filtered = filtered.filter((user) => {
          if (!user.lastSignInDateTimeUtc) return false; // Skip if no lastSignInDateTime

          const updated = new Date(user.lastSignInDateTimeUtc);
          const filterDate = new Date(filters.lastSignInDateTimeUtc);
          return updated < filterDate;
        });
      }

      setFilteredUsers(filtered);
    };

    applyFilterAndSort();
  }, [search, users, sortColumn, sortOrder, filters]);

  const handleGetUsers = async () => {
    try {
      const accessToken = await getAccessToken();
      const newUsers = await getUsersByTenant(tenantPublicId, accessToken);

      const formattedUsers = newUsers.map((user) => ({
        ...user,
        accountTypePrice: PriceFormatter(user.accountTypePrice),

        licenses: user.licenses
          ? Object.values(user.licenses)
            .map((l) => l.trim())
            .filter((l) => l && l.length > 0)
            .join(", ")
          : "No licenses",

        roles: Array.isArray(user.roles) ? user.roles.join(", ") : "No roles",
        syncEnabled: user.onPremSyncEnabled
          ? "Yes"
          : "No",
      }));

      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error(error);
      setUsersLoaded(true);
    }
  };

  useEffect(() => {
    const fetchBillingGroups = async () => {
      try {
        const accessToken = await getAccessToken();
        const groups = await getBillingGroupsByAgreement(
          agreementPublicId,
          accessToken
        );
        setBillingGroups(groups);
      } catch (error) {
        console.error("Failed to fetch billing groups:", error);
      }
    };

    fetchBillingGroups();
  }, [agreementPublicId, getAccessToken]);

  useEffect(() => {
    const fetchAccountTypes = async () => {
      try {
        const accessToken = await getAccessToken();
        const at = await getAccountTypesByAgreement(
          agreementPublicId,
          accessToken
        );
        setAccountTypes(at);
      } catch (error) {
        console.error("Failed to fetch account types:", error);
      }
    };

    fetchAccountTypes();
  }, [agreementPublicId, getAccessToken]);

    const billingGroupOptions = (billingGroups ?? []).map(bg => ({
    label: bg.name,
    value: bg.name
  }));

  const accountTypeOptions = (accountTypes ?? []).map((at) => ({
    label: at.name,
    value: at.name,
  }));

  const allLicenses = Array.from(    
    new Set([
      ...users.flatMap((u) =>
        typeof u.licenses === "string"
      ? u.licenses
      .split(",")
      .map((l) => l.trim())
      .filter((l) => l && l.length > 0)
      : []
    ),
    "No licenses"
  ])
);

const licenseOptions = allLicenses.map(l => ({ label: l, value: l}));

const allRoles = Array.from(    
    new Set([
      ...users.flatMap((u) =>
        typeof u.roles === "string"
      ? u.roles
      .split(",")
      .map((r) => r.trim())
      .filter((r) => r && r.length > 0)
      : []
    ),
    "No roles"
  ])
);

const roleOptions = allRoles.map(r => ({ label: r, value: r }));

  const handleDeleteUser = async (userPublicId) => {
    const toastId = toast.loading("Deleting user...");
    try {
      const accessToken = await getAccessToken();
      await deleteUser(userPublicId, accessToken);
      const newUsers = await getUsersByTenant(accessToken);
      setUsers(newUsers);
      toast.success("User deleted", { id: toastId });
    } catch (error) {
      toast.error("Failed to delete user", { id: toastId });
      console.error(error);
    }
  };

  const handleGetAgreement = async () => {
    try {
      const accessToken = await getAccessToken();
      const newAgreement = await getAgreementByPublicId(
        agreementPublicId,
        accessToken
      ); // Fetch the agreement with API call
      setAgreement(newAgreement);
    } catch (error) {
      console.error("Failed to fetch agreement or contact person:", error);
    }
  };

  // The SCO form settings decide whether the customer offboards through Insight (epic #288) or
  // through the old JotForm link. A failure simply hides the menu item.
  const handleGetScoFormSettings = async () => {
    const accessToken = await getAccessToken();
    const settings = await getScoFormSettings(accessToken).catch(() => null);
    setScoFormSettings(settings);
  };

  const scoEnabled = !!agreement?.scoFormsEnabled;
  // Add user is always offered (PBI #386): an agreement's own create-user form wins only
  // when SCO forms are off; otherwise the create-user route picks the SCO or production page.
  const useInternalCreatePage = scoEnabled || !agreement?.createUserFormUrl;

  const handleOpenCreateUserModal = () => {
    setIsCreateUserOpen(true);
    setIsReady(false);

    setTimeout(() => {
      setIsReady(true);
    }, 2000); // 2 sek delay
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Users"} loading={!usersLoaded} />
            </div>

            <div className={`${!usersLoaded ? "loading" : ""} page-content pb-[20rem]`}>
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Users</h2>
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  {/* User actions live here as visible buttons rather than in a
                      corner menu — Diflex feedback from the SCO staging round. */}
                  <div className="flex items-center gap-4">
                    <button
                      className="btn btn-outline-primary-small"
                      onClick={handleOpenModal}
                      disabled={selectedUsers.length === 0}
                    >
                      Update billing group
                    </button>

                    <Link
                      to={
                        useInternalCreatePage
                          ? `/${agreementPublicId}/${tenantPublicId}/users/create-user`
                          : agreement.createUserFormUrl
                      }
                      target={useInternalCreatePage ? undefined : "_blank"}
                      className="btn btn-outline-primary-small"
                    >
                      <FontAwesomeIcon
                        icon={faCirclePlus}
                        className="mr-2 text-secondary-400"
                      />
                      Add user
                    </Link>

                    {scoEnabled ? (
                      <button
                        className="btn btn-outline-primary-small"
                        onClick={() =>
                          navigate(
                            `/${agreementPublicId}/${tenantPublicId}/users/${selectedUsers[0]}`
                          )
                        }
                        disabled={selectedUsers.length !== 1}
                        title={selectedUsers.length !== 1 ? "Select exactly one user" : undefined}
                      >
                        <FontAwesomeIcon icon={faPenToSquare} className="mr-2" />
                        Edit user
                      </button>
                    ) : (
                      agreement?.updateUserFormUrl && (
                        <Link
                          to={agreement.updateUserFormUrl}
                          target="_blank"
                          className="btn btn-outline-primary-small"
                        >
                          <FontAwesomeIcon icon={faPenToSquare} className="mr-2" />
                          Edit user
                        </Link>
                      )
                    )}

                    {scoEnabled && scoFormSettings?.supportsOffboarding ? (
                      <button
                        className="btn btn-outline-primary-small"
                        onClick={() =>
                          navigate(
                            `/${agreementPublicId}/${tenantPublicId}/users/offboard-user/${selectedUsers[0]}`
                          )
                        }
                        disabled={selectedUsers.length !== 1}
                        title={selectedUsers.length !== 1 ? "Select exactly one user" : undefined}
                      >
                        <FontAwesomeIcon
                          icon={faCircleMinus}
                          className="mr-2 text-error-400"
                        />
                        Offboard user
                      </button>
                    ) : (
                      agreement?.deleteUserFormUrl && (
                        <Link
                          to={agreement.deleteUserFormUrl}
                          target="_blank"
                          className="btn btn-outline-primary-small"
                        >
                          <FontAwesomeIcon
                            icon={faCircleMinus}
                            className="mr-2 text-error-400"
                          />
                          Delete user
                        </Link>
                      )
                    )}
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <button
                        className="btn-outline-primary-small icon"
                        onClick={() => setIsFilterOpen((prev) => !prev)}
                      >
                        <FontAwesomeIcon
                          icon={faFilterList}
                          className="mr-2 text-lg"
                        />
                        Filter
                      </button>
                      {isFilterOpen && (
                        <div className="absolute top-12 z-10">
                          <div className="p-4 bg-white flex gap-4 flex-col w-[22rem] border rounded-xl">
                            <h3>Filter users</h3>

                            <Dropdown
                              label="On-premises sync"
                              options={onPremOptions.map((opt) => opt.label)}
                              value={
                                onPremOptions.find(
                                  (opt) =>
                                    opt.value === filters.onPremSyncEnabled
                                )?.label || "All"
                              }
                              onSelect={(selectedLabel) => {
                                const selectedOption = onPremOptions.find(
                                  (opt) => opt.label === selectedLabel
                                );
                                setFilters((prev) => ({
                                  ...prev,
                                  onPremSyncEnabled: selectedOption.value,
                                }));
                              }}
                            />

                            <MultipleSelectDropdown
                              label="Billing group"
                              options={billingGroupOptions}
                              selected={filters.billingGroup}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  billingGroup: selected,
                                }))
                              }
                              searchable={false}
                            />

                            <MultipleSelectDropdown
                              label="Account type"
                              options={accountTypeOptions}
                              selected={filters.accountType}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  accountType: selected,
                                }))
                              }
                              searchable={false}
                            />

                            <InputField
                              label="Not active since"
                              type="date"
                              value={filters.lastSignInDateTimeUtc}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  lastSignInDateTimeUtc: e.target.value,
                                }))
                              }
                            />
                            <MultipleSelectDropdown
                              label="User role"
                              options={roleOptions}
                              selected={filters.role}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  role: selected,
                                }))
                              }
                              searchable={false}
                            />
                            <MultipleSelectDropdown
                              label="Licenses"
                              options={licenseOptions}
                              selected={filters.licenses}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  licenses: selected,
                                }))
                              }
                              searchable={false}
                            />

                            <button
                              className="btn-outline-primary-small"
                              onClick={() =>
                                setFilters({
                                  billingGroup: [],
                                  accountType: [],
                                  lastSignInDateTimeUtc: "",
                                  role: [],
                                  licenses: [],
                                  onPremSyncEnabled: null,
                                })
                              }
                            >
                              Clear filter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Manager, Email "}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredUsers}
                    selectedRows={selectedUsers} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedUsers} // Ensure selection updates correctly
                    noDelete={true}
                    columns={[
                      { key: "displayName", label: "Name" },
                      { key: "roles", label: "Role" },
                      { key: "userPrincipalName", label: "Principal name" },
                      { key: "accountTypeName", label: "Account type" },
                      { key: "accountTypePrice", label: "Account type price" },
                      { key: "licenses", label: "Licenses" },
                      { key: "billingGroupName", label: "Billing group" },
                      { key: "syncEnabled", label: "On prem sync" },
                      { key: "officeLocation", label: "Office location" },
                      { key: "country", label: "Country" },
                      { key: "mobilePhone", label: "Phone" },
                      { key: "jobTitle", label: "Job title" },
                      { key: "managerDisplayName", label: "Manager" },
                      { key: "lastSignInDateTimeUtc", label: "Last login" },
                    ]}
                    primaryKey="userPublicId" // Match this with userPublicId
                    basePath={`/${agreementPublicId}/${tenantPublicId}/users`}
                    /* handleDelete={handleDeleteUser} */
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">Change Billing Group</h2>

          <Dropdown
            label="Select Billing Group"
            options={billingGroups.map((group) => group.name)}
            value={
              billingGroups.find(
                (group) => group.billingGroupPublicId === selectedBillingGroup
              )?.name || ""
            }
            onSelect={handleSelectBillingGroup}
          />

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={isCreateUserOpen} setIsOpen={setIsCreateUserOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">
            Redirecting to user-form
          </h2>

          {!isReady ? (
            <span>Loading user form...</span>
          ) : (
            <>
              <span>Proceed to user form.</span>
              <div className="flex mt-4 gap-4">
                <button
                  className="btn-outline-primary"
                  onClick={() => setIsCreateUserOpen(false)}
                >
                  Cancel
                </button>
                <a
                  href={agreement?.createUserFormUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary"
                >
                  To user form
                </a>
              </div>
            </>
          )}
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default UsersPage;
